import { useState, useMemo } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import API from "../../services/api";
import { Toast } from "../../shared/Toast";
import { SlDrawer } from "@shoelace-style/shoelace/dist/react";
import { debouncePromise } from "../../utils/Debounce";
import AsyncSelect from "react-select/async";

const REPORT_OPTIONS = [
  { label: "Category", value: "1" },
  { label: "Location", value: "2" },
  { label: "Brand Merging", value: "3" },
  { label: "Pricing", value: "4" },
  { label: "Adtype", value: "5" },
  { label: "Page", value: "6" },
];

function ReportBirdview({
  open,
  setOpen,
  reportId,
  image_url,
  source,
  reportData,
}) {
  const [previewMediaMap, setPreviewMediaMap] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  function handleRequestClose(event) {
    event.preventDefault();
    event.stopPropagation();
    if (event.detail.source !== "overlay") {
      setOpen(false);
    }
  }
  const form1 = useForm({
    mode: "onSubmit",
    defaultValues: {
      reports: [
        {
          type: source === "PageRates" ? "4" : "1",
          description: "",
          brand_name: null,
          media_url: null,
        },
      ],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control: form1.control,
    name: "reports",
  });

  const isTypeSelected = (type) => fields.some((item) => item.type === type);

  const addReportType = (type) => {
    if (isTypeSelected(type)) return;
    append({
      type,
      description: "",
      brand_name: null,
      media_url: null,
    });
  };

  const removeReportType = (type) => {
    const index = fields.findIndex((item) => item.type === type);
    if (index === -1) return;
    const fieldId = fields[index]?.id;
    remove(index);
    setMediaMap((prev) => {
      const next = { ...prev };
      if (fieldId) {
        delete next[fieldId];
      }
      return next;
    });
    setPreviewMediaMap((prev) => {
      const next = { ...prev };
      if (fieldId && next[fieldId]?.url) {
        URL.revokeObjectURL(next[fieldId].url);
      }
      if (fieldId) {
        delete next[fieldId];
      }
      return next;
    });
  };

  const handleOptionChange = (option, checked) => {
    if (checked) {
      addReportType(option.value);
    } else {
      removeReportType(option.value);
    }
  };

  const addIssue = async (data) => {
    if (!data?.reports?.length) {
      Toast("error", "Select at least one report type");
      return;
    }
    try {
      const issuesPayload = await Promise.all(
        data.reports.map(async (report, index) => {
          const fieldId = fields[index]?.id;
          const mediaFile = fieldId ? mediaMap[fieldId] : null;
          const media_url =
            report.type === "4" && mediaFile
              ? await uploadMedia(mediaFile)
              : undefined;
          return {
            type: report.type,
            description: report.description,
            media: media_url,
            brand_name: report.type === "3" ? report.brand_name : undefined,
            report_type : source ? 'PageRates' : 'birdview'
          };
        })
      );
      let res = await API({
        url: "/help/addIssue",
        method: "POST",
        responseType: "json",
        data: {
          reportId: reportId,
          issues: issuesPayload,
        },
      });
      if (res.code === 200) {
        setOpen(false);
        setPreviewMediaMap({});
        setMediaMap({});
        Toast("success", "Ticket Added Successfully");
        form1.reset({
          reports: [
            {
              type: "1",
              description: "",
              brand_name: null,
              media_url: null,
            },
          ],
        });
      }
    } catch (error) {
      console.error(error);
    }
  };
  const uploadMedia = async (file) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append("files", file);
    try {
      let res = await API({
        url: "/upload/help",
        method: "POST",
        responseType: "json",
        headers: { "Content-Type": "multipart/form-data" },
        data: formData,
      });
      if (res.code === 200) {
        return res.data;
      }
    } catch (error) {
      console.error(error, "Error while uplaoding");
      Toast("error", "Error while uploading");
    }
  };

  const loadBrands = async (search) => {
    try {
      let res = await API({
        url: "/help/brands",
        method: "POST",
        responseType: "json",
        data: {
          search: search,
        },
      });
      if (res.code === 200) {
        return res.data;
      }
    } catch (error) {
      console.error(error);
    }
  };
  const debouncedLoadBrands = useMemo(
    () => debouncePromise(loadBrands, 500),
    []
  );

  const handleMediaPreview = (event, fieldId) => {
    const file = event.target.files?.[0];
    setMediaMap((prev) => {
      const next = { ...prev };
      if (file) {
        next[fieldId] = file;
      } else {
        delete next[fieldId];
      }
      return next;
    });
    setPreviewMediaMap((prev) => {
      const next = { ...prev };
      if (next[fieldId]?.url) {
        URL.revokeObjectURL(next[fieldId].url);
      }
      if (file) {
        const fileURL = URL.createObjectURL(file);
        next[fieldId] = {
          url: fileURL,
          type: file.type.startsWith("video") ? "video" : "image",
        };
      } else {
        delete next[fieldId];
      }
      return next;
    });
  };

  const getTypeLabel = (type) =>
    REPORT_OPTIONS.find((opt) => opt.value === type)?.label || "Report";

  return (
    <>
      {open && (
        <SlDrawer
          label="Report Ad"
          placement="start"
          open={open}
          style={{
            "--size": window.innerWidth <= 767 ? "100%" : "49%",
            "--z-index": "1001",
          }}
          onSlRequestClose={handleRequestClose}
        >
          <div className="row g-3">
            <div className="col-12 col-lg-6">
              <form onSubmit={form1.handleSubmit(addIssue)}>
                <div className="row g-2 mb-2">
                  {REPORT_OPTIONS.map((option) => {
                    const optionId = `report-option-${option.value}`;
                    return (
                      <div
                        key={option.value}
                        className="col-12 col-sm-6 d-flex align-items-center"
                      >
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={optionId}
                            disabled={
                              source === "PageRates" && option.value !== "4"
                            }
                            checked={isTypeSelected(option.value)}
                            onChange={(e) =>
                              handleOptionChange(option, e.target.checked)
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor={optionId}
                          >
                            {option.label}
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <hr></hr>

                {fields.map((field, index) => {
                  const reportType = form1.watch(`reports.${index}.type`);
                  const descriptionValue = form1.watch(
                    `reports.${index}.description`
                  );
                  const previewMedia = previewMediaMap[field.id];

                  return (
                    <div
                      key={field.id}
                      className="border rounded p-3 mb-3 bg-white shadow-sm"
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                          {getTypeLabel(reportType)} Report
                        </h6>
                        <button
                          type="button"
                          className="btn btn-link text-danger p-0"
                          onClick={() => removeReportType(reportType)}
                        >
                          Remove
                        </button>
                      </div>

                      <input
                        type="hidden"
                        {...form1.register(`reports.${index}.type`)}
                        defaultValue={field.type}
                      />

                      {reportType === `3` ? (
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Brand Name
                          </label>
                          <Controller
                            name={`reports.${index}.brand_name`}
                            control={form1.control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <AsyncSelect
                                {...field}
                                cacheOptions
                                loadOptions={debouncedLoadBrands}
                                defaultOptions
                                placeholder="Search Brand..."
                                isClearable
                              />
                            )}
                          />
                          {form1.formState?.errors?.reports?.[index]
                            ?.brand_name && (
                            <p className="text-red-600 text-xs mt-1">
                              {form1.formState?.errors?.reports?.[index]
                                ?.brand_name?.message || "Please select brand"}
                            </p>
                          )}
                        </div>
                      ) : null}

                      {reportType === "4" && (
                        <div className="mb-3">
                          <label
                            className="form-label"
                            htmlFor={`media_url-${field.id}`}
                          >
                            Upload Image / Video
                          </label>
                          <span className="text-red-600">*</span>
                          <input
                            className="form-control"
                            type="file"
                            placeholder="Upload Media"
                            aria-label="media_url"
                            id={`media_url-${field.id}`}
                            {...form1.register(`reports.${index}.media_url`, {
                              required: reportType === "4",
                              onChange: (e) => handleMediaPreview(e, field.id),
                            })}
                          ></input>
                          {form1.formState?.errors?.reports?.[index]
                            ?.media_url && (
                            <p className="text-red-600">Please upload Media</p>
                          )}
                          {previewMedia && (
                            <div className="mt-2 !max-w-[220px]">
                              <div className="border rounded p-2 bg-light">
                                <p className="mb-2 fw-semibold small text-muted">
                                  Uploaded
                                </p>
                                {previewMedia.type === "image" ? (
                                  <img
                                    src={previewMedia.url}
                                    alt="Preview"
                                    className="w-full max-h-48 rounded border"
                                  />
                                ) : (
                                  <video
                                    src={previewMedia.url}
                                    controls
                                    className="w-full h-auto rounded border"
                                  />
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mb-3">
                        <label
                          htmlFor={`description-${field.id}`}
                          className="form-label"
                        >
                          Description
                        </label>
                        <span
                          className={`${
                            descriptionValue?.length > 300
                              ? "text-red-600"
                              : "text-green-500"
                          }`}
                        >
                          {300 - (descriptionValue?.length || 0)}
                        </span>
                        <textarea
                          className="form-control"
                          id={`description-${field.id}`}
                          rows="3"
                          {...form1.register(`reports.${index}.description`, {
                            maxLength: 300,
                          })}
                        ></textarea>
                        {form1.formState?.errors?.reports?.[index]?.description
                          ?.type === "maxLength" && (
                          <p className="text-red-600">Too many Characthers</p>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div className="mb-3">
                  <button
                    className="btn btn-primary w-full"
                    type="submit"
                    disabled={form1.formState?.isSubmitting}
                  >
                    {form1.formState?.isSubmitting && (
                      <span
                        className="spinner-grow spinner-grow-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    )}
                    {form1.formState?.isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
            <div className="col-12 col-lg-6">
              <div className="border rounded p-3 bg-light h-100">
                <p className="mb-2 fw-semibold small text-muted">
                  Current {source === "PageRates" ? "Page Rate" : "Ad"}{" "}
                </p>
                {source ? (
                  <>
                    <p>NewsPaper : {reportData?.newspaper_name.toUpperCase()}</p>
                    <p>LocationType :{reportData?.location_type.toUpperCase()}</p>
                    <p>Location: {reportData?.location.toUpperCase()}</p>
                    <p>Page Type: {reportData?.page_type.toUpperCase()}</p>
                    <p>Ad Type: {reportData?.ad_type.toUpperCase() || "-"}</p>
                    <p>AdSize: {reportData?.ad_size.toUpperCase()}</p>
                    <p>Color Price: {reportData?.price}</p>
                    <p>BW Price: {reportData?.bw_price}</p>
                  </>
                ) : (
                  <img
                    src={image_url}
                    alt="Current Ad"
                    className="w-100 rounded border"
                    style={{ maxHeight: "520px", objectFit: "contain" }}
                  />
                )}
              </div>
            </div>
          </div>
        </SlDrawer>
      )}
    </>
  );
}

export default ReportBirdview;
